import React, { useState } from 'react';
import { ChevronRight, Folder, FolderOpen, FileText, Archive, CalendarDays } from 'lucide-react';

const fmtDate = (d) => (d ? new Date(d).toLocaleDateString('en-ZA', { day: 'numeric', month: 'short', year: 'numeric' }) : '');
const kb = (n) => (n > 1048576 ? `${(n / 1048576).toFixed(1)} MB` : `${Math.max(1, Math.round((n || 0) / 1024))} KB`);

const Node = ({ icon: Icon, openIcon: OpenIcon, label, meta, depth, defaultOpen, children, testid }) => {
  const [open, setOpen] = useState(!!defaultOpen);
  const I = open && OpenIcon ? OpenIcon : Icon;
  return (
    <div>
      <button type="button" onClick={() => setOpen(!open)} style={{ paddingLeft: depth * 18 + 12 }} data-testid={testid}
        className="w-full flex items-center gap-2 pr-4 py-2 text-left text-sm hover:bg-stone-50 transition-colors">
        <ChevronRight size={14} className={`text-stone-400 transition-transform shrink-0 ${open ? 'rotate-90' : ''}`}/>
        <I size={16} className="text-amber-600 shrink-0"/>
        <span className="font-medium text-stone-800 truncate">{label}</span>
        {meta && <span className="ml-auto text-xs text-stone-400 whitespace-nowrap">{meta}</span>}
      </button>
      {open && children}
    </div>
  );
};

const FileRow = ({ f, href, depth }) => (
  <a href={href(f)} target="_blank" rel="noreferrer" style={{ paddingLeft: depth * 18 + 34 }} data-testid={`folder-file-${f.id}`}
    className="flex items-start gap-2 pr-4 py-2 text-sm hover:bg-emerald-50/60 transition-colors">
    <FileText size={15} className="text-emerald-700 mt-0.5 shrink-0"/>
    <span className="min-w-0 flex-1">
      <span className="block text-stone-900 truncate hover:underline">{f.filename}</span>
      {f.note && <span className="block text-xs text-stone-600 whitespace-pre-wrap">{f.note}</span>}
      <span className="block text-[11px] text-stone-400">{f.deadlineTitle ? `For: ${f.deadlineTitle} · ` : ''}Uploaded {fmtDate(f.createdAt)} · {kb(f.size)}</span>
    </span>
  </a>
);

export const FolderTree = ({ data, fileHref, archiveActions, emptyText }) => (
  <div className="rounded-2xl bg-white border border-stone-200 overflow-hidden" data-testid="folder-tree">
    <div className="px-5 py-4 border-b border-stone-100">
      <div className="font-semibold text-stone-900">Monthly document files</div>
      <div className="text-xs text-stone-500 mt-0.5 flex items-center gap-1.5"><CalendarDays size={12}/> Financial year ends {data.fyEndMonth} · current year {data.currentFy.label} ({data.currentFy.range})</div>
    </div>
    {data.years.length === 0 && data.archives.length === 0 && <div className="p-8 text-center text-sm text-stone-500" data-testid="folder-empty">{emptyText}</div>}
    <div className="py-1">
      {data.years.map((y, yi) => (
        <Node key={y.fy} icon={Folder} openIcon={FolderOpen} label={`${y.label} · ${y.range}`} meta={`${y.count} file${y.count === 1 ? '' : 's'}`} depth={0} defaultOpen={yi === 0} testid={`folder-fy-${y.fy}`}>
          {y.categories.map((c) => (
            <Node key={c.name} icon={Folder} openIcon={FolderOpen} label={c.name} meta={c.count} depth={1} testid={`folder-cat-${y.fy}-${c.name}`}>
              {c.months.map((m) => (
                <Node key={m.period} icon={Folder} openIcon={FolderOpen} label={m.label} meta={m.files.length} depth={2} testid={`folder-month-${y.fy}-${c.name}-${m.period}`}>
                  {m.files.map((f) => <FileRow key={f.id} f={f} href={fileHref} depth={3}/>)}
                </Node>
              ))}
            </Node>
          ))}
        </Node>
      ))}
      {data.archives.map((a) => (
        <div key={a.id} className="flex flex-wrap items-center gap-2 px-4 py-3 border-t border-stone-100 bg-stone-50/60" data-testid={`archive-${a.fy}`}>
          <Archive size={16} className="text-stone-500"/>
          <span className="text-sm font-medium text-stone-800">{a.label} · {a.range}</span>
          <span className="text-xs text-stone-500">Archived {fmtDate(a.createdAt)} · {a.fileCount} file{a.fileCount === 1 ? '' : 's'} · {kb(a.size)}{a.downloadedAt ? ` · downloaded ${fmtDate(a.downloadedAt)}` : ''}</span>
          <span className="ml-auto">{archiveActions ? archiveActions(a) : <span className="text-xs text-stone-500">Ask us if you need a copy</span>}</span>
        </div>
      ))}
    </div>
  </div>
);
